import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Form, Button, Table } from 'react-bootstrap';
import API from '../services/api';
import ApprovalChip from './ApprovalChip';
import { useAuth } from '../context/AuthContext';
import UNIDADES_MEDIDA from '../data/unidadesMedida';
import { PRODUCT_UNIT_CODES, DEFAULT_UNIT_CODE } from '../data/productUnits';
import CategoryModal from './CategoryModal';

const ADD_CATEGORY_OPTION = '__add__';

const emptyForm = {
  name: '', sku: '', description: '', category: '', unidadMedida: DEFAULT_UNIT_CODE, price: '', cost: '', itbisExento: false, itbisRate: 0.18,
};

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const ProductsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // cost and margin are for admins only
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const loadProducts = () => {
    API.get('/products').then(res => setProducts(res.data)).catch(err => console.error('Error loading products:', err));
  };

  const loadCategories = () => {
    API.get('/categories').then(res => setCategories(res.data)).catch(err => console.error('Error loading categories:', err));
  };

  const loadStats = () => {
    API.get('/products/stats').then(res => setStats(res.data)).catch(err => console.error('Error loading product stats:', err));
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadStats();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    if (value === ADD_CATEGORY_OPTION) {
      setShowCategoryModal(true);
      return;
    }
    setForm(prev => ({ ...prev, category: value }));
  };

  const handleCategoryCreated = (category) => {
    setCategories(prev => [...prev, category].sort((a, b) => a.name.localeCompare(b.name)));
    setForm(prev => ({ ...prev, category: category.name }));
    setShowCategoryModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...form, price: Number(form.price), cost: Number(form.cost) || 0, itbisRate: Number(form.itbisRate), unidadMedida: Number(form.unidadMedida) };
      if (editingId) {
        await API.put(`/products/${editingId}`, payload);
      } else {
        await API.post('/products', payload);
      }
      setForm(emptyForm);
      setEditingId(null);
      loadProducts();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar el producto.');
    }
  };

  const handleEdit = (product) => {
    setError('');
    setEditingId(product._id);
    setForm({
      name: product.name || '',
      sku: product.sku || '',
      description: product.description || '',
      category: product.category || '',
      unidadMedida: product.unidadMedida ?? DEFAULT_UNIT_CODE,
      price: product.price ?? '',
      cost: product.cost ?? '',
      itbisExento: !!product.itbisExento,
      itbisRate: product.itbisRate ?? 0.18,
    });
  };

  const handleCancelEdit = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleDelete = async (id) => {
    try {
      const res = await API.delete(`/products/${id}`);
      // 202: a sales user asked an admin to delete it — the product is still here until they approve.
      if (res.status !== 202 && id === editingId) handleCancelEdit();
      loadProducts();
      loadStats();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  // Accent-insensitive match on name, item code or category.
  const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const term = norm(search.trim());
  const shownProducts = term ? products.filter(p => [p.name, p.sku, p.description, p.category].some(v => norm(v).includes(term))) : products;

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>Productos</h2>

      <Row className="mb-3">
        {Array.isArray(stats?.topProducts) && [0, 1, 2].map(i => {
          const p = stats?.topProducts?.[i];
          return (
            <Col md={3} key={i}>
              <Card className="shadow-sm p-3 dashboard-card">
                <div className="text-muted small">#{i + 1} más vendido (30 días)</div>
                {p ? (
                  <>
                    <h5 className="mt-2 mb-1">{p.name}</h5>
                    <div style={{ color: 'var(--mm-cyan)', fontWeight: 600 }}>{money(p.revenue)}</div>
                    <div className="text-muted small">{p.quantity} unidades</div>
                  </>
                ) : (
                  <div className="text-muted mt-2">Sin datos todavía.</div>
                )}
              </Card>
            </Col>
          );
        })}
        {!isAdmin && (
          // The top-sellers tiles are admin-only: sales get two tiles that fill the row instead of one lonely tile.
          <Col md={6}>
            <Card className="shadow-sm p-3 dashboard-card">
              <div className="text-muted small">Productos activos</div>
              <h2 className="mt-2 mb-0">{stats?.activeProductCount ?? '—'}</h2>
            </Card>
          </Col>
        )}
        <Col md={isAdmin ? 3 : 6}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Sin ventas en 30 días</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-yellow)' }}>{stats?.noSaleCount ?? '—'}</h2>
            <div className="text-muted small">de {stats?.activeProductCount ?? 0} productos activos</div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={4} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <Card.Title>{editingId ? 'Editar Producto' : 'Nuevo Producto'}</Card.Title>
            {error && <div className="alert alert-danger">{error}</div>}
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col xl={6}>
              <Form.Group className="mb-2">
                <Form.Label>Nombre</Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleChange} required />
              </Form.Group>
                </Col>
                <Col xl={6}>
              <Form.Group className="mb-2">
                <Form.Label>No. de Item</Form.Label>
                <Form.Control name="sku" value={form.sku} onChange={handleChange} />
              </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Descripción <span className="text-muted">(opcional)</span></Form.Label>
                <Form.Control as="textarea" rows={2} maxLength={500} name="description" value={form.description} onChange={handleChange} />
              </Form.Group>
              <Row>
                <Col xl={6}>
              <Form.Group className="mb-2">
                <Form.Label>Categoría</Form.Label>
                <Form.Select className="w-100" name="category" value={form.category} onChange={handleCategoryChange} required>
                  <option value="" disabled>Selecciona una categoría...</option>
                  {categories.map(c => (
                    <option key={c._id} value={c.name}>{c.name}</option>
                  ))}
                  <option value={ADD_CATEGORY_OPTION}>+ Agregar categoría...</option>
                </Form.Select>
              </Form.Group>
                </Col>
                <Col xl={6}>
              <Form.Group className="mb-2">
                <Form.Label>Unidad de Medida</Form.Label>
                <Form.Select className="w-100" name="unidadMedida" value={form.unidadMedida} onChange={handleChange}>
                  {/* Only the units we sell in; a product saved with another one keeps showing its own. */}
                  {UNIDADES_MEDIDA.filter(u => PRODUCT_UNIT_CODES.includes(u.code) || u.code === Number(form.unidadMedida)).map(u => (
                    <option key={u.code} value={u.code}>{u.name} ({u.abbr})</option>
                  ))}
                </Form.Select>
              </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xl={isAdmin ? 6 : 12}>
              <Form.Group className="mb-2">
                <Form.Label>Precio de lista (DOP)</Form.Label>
                <Form.Control type="number" step="0.01" name="price" value={form.price} onChange={handleChange} required />
                <Form.Text className="text-muted">
                  Este es el precio base. Los precios especiales por cliente se gestionan desde la página de Clientes.
                </Form.Text>
              </Form.Group>
                </Col>
              {isAdmin && <Col xl={6}><Form.Group className="mb-2">
                <Form.Label>Costo (DOP)</Form.Label>
                <Form.Control type="number" step="0.01" name="cost" value={form.cost} onChange={handleChange} />
                <Form.Text className="text-muted">
                  Lo que te cuesta adquirir/producir una unidad. Se usa solo para calcular margen — no aparece en la factura.
                </Form.Text>
              </Form.Group></Col>}
              </Row>
              <Form.Check className="mb-2" label="Exento de ITBIS" name="itbisExento" checked={form.itbisExento} onChange={handleChange} />
              {!form.itbisExento && (
                <Form.Group className="mb-3">
                  <Form.Label>Tasa ITBIS</Form.Label>
                  <Form.Select name="itbisRate" value={form.itbisRate} onChange={handleChange}>
                    <option value={0.18}>18%</option>
                    <option value={0.16}>16%</option>
                  </Form.Select>
                </Form.Group>
              )}
              <div className="d-flex gap-2">
                {editingId && (
                  <Button type="button" variant="outline-secondary" className="w-100" onClick={handleCancelEdit}>Cancelar</Button>
                )}
                <Button type="submit" className="w-100">{editingId ? 'Guardar Cambios' : 'Guardar Producto'}</Button>
              </div>
            </Form>
          </Card>
        </Col>
        <Col md={8} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <Card.Title className="mb-0">Catálogo <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>· {shownProducts.length}</span></Card.Title>
              <Form.Control style={{ maxWidth: 400 }} placeholder="Buscar por nombre, código, categoría o descripción..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>No. de Item</th>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Unidad de Medida</th>
                  <th>Precio</th>
                  {isAdmin && <th>Costo</th>}
                  {isAdmin && <th>Margen</th>}
                  <th>ITBIS</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shownProducts.map(p => {
                  const margin = p.price > 0 ? ((p.price - (p.cost || 0)) / p.price) * 100 : null;
                  return (
                    <tr key={p._id}>
                      <td>{p.sku || '—'}</td>
                      <td>
                        {p.name} <ApprovalChip entityId={p._id} className="ms-1" />
                        {p.description && <div className="text-muted small" title={p.description} style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>}
                      </td>
                      <td>{p.category || '—'}</td>
                      <td>{UNIDADES_MEDIDA.find(u => u.code === p.unidadMedida)?.name || p.unidadMedida}</td>
                      <td>{money(p.price)}</td>
                      {isAdmin && <td>{money(p.cost)}</td>}
                      {isAdmin && <td>{margin === null ? '—' : `${margin.toFixed(0)}%`}</td>}
                      <td>{p.itbisExento ? 'Exento' : `${p.itbisRate * 100}%`}</td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <Button size="sm" variant="outline-secondary" title="Editar" onClick={() => handleEdit(p)}><i className="bi bi-pencil" /></Button>
                          <Button size="sm" variant="outline-danger" title="Eliminar" onClick={() => handleDelete(p._id)}><i className="bi bi-trash" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {shownProducts.length === 0 && (
                  <tr><td colSpan={isAdmin ? 9 : 7} className="text-muted text-center">{search ? `Ningún producto coincide con "${search}".` : 'Sin productos todavía.'}</td></tr>
                )}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>

      {showCategoryModal && (
        <CategoryModal onClose={() => setShowCategoryModal(false)} onCreated={handleCategoryCreated} />
      )}
    </div>
  );
};

export default ProductsPage;
